import { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import * as api from "../api/client";
import { BRAND, BrandMark } from "../components/BrandMark";

const WS_KEY = "ql-workspace";
const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/^[^a-z]+/, "").slice(0, 30);

const BENEFITS = [
  ["M4 19V5m0 14h16M8 15l3-4 3 2 5-6", "Answers in seconds", "Ask in plain words. QLens queries your data and replies with exact figures, charts and tables."],
  ["M3 17l5-5 4 4 8-9M15 7h5v5", "Predictions and forecasts", "Train models on your own tables, see what drives results and project the months ahead."],
  ["M4 5h16v14H4zM4 10h16M10 10v9", "Dashboards and reports", "Turn answers into live dashboards, then share them or export to PDF, PowerPoint and Excel."],
  ["M4 6c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3zm0 0v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6", "Connect your sources", "PostgreSQL, SQLite and MongoDB today, plus MySQL, SQL Server and Oracle in beta. Read-only, always."],
];

const SECURITY = [
  "Your workspace is isolated in its own database schema",
  "Access by role, down to individual columns and documents",
  "Passwords and API keys encrypted at rest",
  "Every question traced, every admin action audited",
  "Option to run on your own AI model, so data never leaves your servers",
];

function Glyph({ d }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

export default function LoginPage() {
  const { login, refresh } = useAuth();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [mode, setMode] = useState(params.get("signup") ? "signup" : "signin");
  const [cfg, setCfg] = useState({ publicSignup: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [workspace, setWorkspace] = useState(() => params.get("ws") || localStorage.getItem(WS_KEY) || "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [sign, setSign] = useState({ workspaceName: "", workspace: "", username: "", password: "", contact: "" });
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    api.platformConfig().then(setCfg).catch(() => {});
  }, []);

  async function submitSignIn(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const ws = workspace.trim().toLowerCase();
    try {
      await login(username.trim(), password, ws);
      if (ws) localStorage.setItem(WS_KEY, ws);
      else localStorage.removeItem(WS_KEY);
    } catch (err) {
      setError(err?.message || "Sign-in failed. Please check your details.");
    } finally {
      setBusy(false);
    }
  }

  async function submitSignUp(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.signup({ ...sign, workspace: sign.workspace.trim().toLowerCase() });
      localStorage.setItem(WS_KEY, r.workspace);
      await refresh();
    } catch (err) {
      setError(err?.message || "Could not create the workspace.");
    } finally {
      setBusy(false);
    }
  }

  const setS = (k) => (e) => {
    const v = e.target.value;
    setSign((s) => {
      const next = { ...s, [k]: v };
      if (k === "workspaceName" && !slugTouched) next.workspace = slugify(v);
      return next;
    });
    if (k === "workspace") setSlugTouched(true);
  };

  return (
    <main className="ql">
      <section className="ql-story">
        <div className="ql-story-in">
          <div className="ql-brand">
            <BrandMark size={40} />
            <div>
              <div className="ql-brand-name">{BRAND.name}</div>
              <div className="ql-brand-by">by {BRAND.company}</div>
            </div>
          </div>
          <h2 className="ql-headline">Your business data, answered.</h2>
          <p className="ql-lede">One governed workspace to ask questions, forecast what's next and share what matters, for every team in your company.</p>
          <ul className="ql-benefits">
            {BENEFITS.map(([icon, title, body]) => (
              <li key={title}>
                <span className="ql-b-ic"><Glyph d={icon} /></span>
                <span><strong>{title}</strong><small>{body}</small></span>
              </li>
            ))}
          </ul>
          <div className="ql-secure">
            <div className="ql-secure-h">
              <Glyph d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
              <span>Your data stays yours</span>
            </div>
            <ul>
              {SECURITY.map((s) => <li key={s}><Glyph d="M5 12.5l4.5 4.5L19 7.5" />{s}</li>)}
            </ul>
          </div>
        </div>
      </section>

      <section className="ql-panel">
        <div className="ql-card">
          <div className="ql-mobile-brand"><BrandMark size={40} /></div>
          {cfg.publicSignup && (
            <div className="ql-switch" role="tablist">
              <button type="button" role="tab" aria-selected={mode === "signin"} className={mode === "signin" ? "on" : ""}
                      onClick={() => { setMode("signin"); setError(""); }}>Sign in</button>
              <button type="button" role="tab" aria-selected={mode === "signup"} className={mode === "signup" ? "on" : ""}
                      onClick={() => { setMode("signup"); setError(""); }}>Create workspace</button>
            </div>
          )}

          {mode === "signin" ? (
            <>
              <h1>Sign in to {BRAND.name}</h1>
              <p className="ql-sub">Use the workspace your company was given.</p>
              {error && <div className="ql-alert" role="alert">{error}</div>}
              <form onSubmit={submitSignIn}>
                <label className="ql-field">
                  <span>Workspace</span>
                  <div className="ql-ws">
                    <input value={workspace} onChange={(e) => setWorkspace(e.target.value)} placeholder="your-company"
                           autoCapitalize="none" autoCorrect="off" spellCheck="false" />
                  </div>
                  <small>Leave blank for the main workspace.</small>
                </label>
                <label className="ql-field">
                  <span>Username</span>
                  <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username"
                         autoFocus={!!workspace} required />
                </label>
                <label className="ql-field">
                  <span>Password</span>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                         autoComplete="current-password" required />
                </label>
                <button type="submit" disabled={busy} className="ql-submit">
                  {busy ? <><Spinner animation="border" size="sm" /> Signing in…</> : "Sign in"}
                </button>
              </form>
            </>
          ) : (
            <>
              <h1>Create your workspace</h1>
              <p className="ql-sub">Start a free trial. Your workspace is private to your company from the first minute.</p>
              {error && <div className="ql-alert" role="alert">{error}</div>}
              <form onSubmit={submitSignUp}>
                <label className="ql-field">
                  <span>Company name</span>
                  <input value={sign.workspaceName} onChange={setS("workspaceName")} maxLength={80} required placeholder="Acme Industries" />
                </label>
                <label className="ql-field">
                  <span>Workspace URL</span>
                  <div className="ql-ws"><input value={sign.workspace} onChange={setS("workspace")} maxLength={30} required
                                                 placeholder="acme" autoCapitalize="none" spellCheck="false" /></div>
                  <small>Your team signs in with this. Lowercase letters, digits and dashes.</small>
                </label>
                <div className="ql-two">
                  <label className="ql-field">
                    <span>Admin username</span>
                    <input value={sign.username} onChange={setS("username")} maxLength={64} required autoComplete="username" />
                  </label>
                  <label className="ql-field">
                    <span>Password</span>
                    <input type="password" value={sign.password} onChange={setS("password")} required autoComplete="new-password" />
                  </label>
                </div>
                <small className="ql-hint">Password: at least 10 characters, with three of lowercase, uppercase, digits and symbols.</small>
                <label className="ql-field">
                  <span>Work email <em>optional</em></span>
                  <input type="email" value={sign.contact} onChange={setS("contact")} maxLength={200} placeholder="you@company.com" />
                </label>
                <button type="submit" disabled={busy} className="ql-submit">
                  {busy ? <><Spinner animation="border" size="sm" /> Creating your workspace…</> : "Create workspace"}
                </button>
              </form>
            </>
          )}
        </div>
        <p className="ql-new">
          New to QLens? <a href="/">See what it does</a>
          <span aria-hidden="true">·</span>
          <a href="https://aireido.com/contact/">Book a free demo</a>
        </p>
        <p className="ql-foot">© {new Date().getFullYear()} {BRAND.company}</p>
      </section>

      <style>{`
        :root { --ql-navy: #0E1E26; --ql-navy-2: #14303b; --ql-red: #E8381D; --ql-red-hover: #cf2b12; --ql-line: #d9d4ca; --ql-slate: #5b6b75; }
        html, body, #root { height: 100%; margin: 0; }
        .ql { min-height: 100dvh; display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr); font-family: 'Inter', system-ui, sans-serif; background: #f4f1ea; }
        .ql-story { position: relative; overflow: hidden; color: #e8eef1;
          background: radial-gradient(700px 380px at 110% -10%, rgba(232,56,29,.32), transparent 60%),
                      radial-gradient(600px 400px at -20% 110%, rgba(56,189,248,.14), transparent 60%),
                      linear-gradient(160deg, var(--ql-navy) 0%, var(--ql-navy-2) 100%);
          display: flex; align-items: center; padding: 48px clamp(28px, 5vw, 72px); }
        .ql-story::after { content: ""; position: absolute; inset: 0; pointer-events: none; opacity: .5;
          background-image: linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px);
          background-size: 44px 44px; mask-image: linear-gradient(180deg, transparent, #000 30%, #000 70%, transparent); }
        .ql-story-in { position: relative; z-index: 1; max-width: 560px; }
        .ql-brand { display: flex; align-items: center; gap: 12px; margin-bottom: 36px; }
        .ql-brand-name { font-size: 22px; font-weight: 800; letter-spacing: -.4px; color: #fff; }
        .ql-brand-by { font-size: 12px; color: #9fb1ba; }
        .ql-headline { font-size: clamp(30px, 3.2vw, 42px); line-height: 1.1; font-weight: 800; letter-spacing: -1px; margin: 0 0 14px; color: #fff; }
        .ql-lede { font-size: 16px; line-height: 1.55; color: #b9c8cf; margin: 0 0 30px; max-width: 480px; }
        .ql-benefits { list-style: none; padding: 0; margin: 0 0 28px; display: grid; gap: 16px; }
        .ql-benefits li { display: flex; gap: 14px; align-items: flex-start; }
        .ql-b-ic { flex: none; width: 38px; height: 38px; border-radius: 10px; display: grid; place-items: center; background: rgba(255,255,255,.07); border: 1px solid rgba(255,255,255,.1); color: #ff8a70; }
        .ql-benefits strong { display: block; font-size: 15px; color: #fff; margin-bottom: 2px; }
        .ql-benefits small { display: block; font-size: 13.5px; line-height: 1.5; color: #a9bac2; }
        .ql-secure { border: 1px solid rgba(255,255,255,.12); background: rgba(255,255,255,.04); border-radius: 12px; padding: 16px 18px; }
        .ql-secure-h { display: flex; align-items: center; gap: 8px; font-weight: 700; color: #fff; margin-bottom: 10px; font-size: 14.5px; }
        .ql-secure-h svg { color: #5eead4; }
        .ql-secure ul { list-style: none; margin: 0; padding: 0; display: grid; gap: 7px; }
        .ql-secure li { display: flex; gap: 8px; align-items: flex-start; font-size: 13.5px; color: #c4d1d7; }
        .ql-secure li svg { flex: none; width: 16px; height: 16px; color: #5eead4; margin-top: 2px; }
        .ql-panel { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 20px; }
        .ql-card { width: 100%; max-width: 440px; background: #fff; border: 1px solid #e6e1d9; border-radius: 14px; padding: 32px 34px 30px; box-shadow: 0 18px 40px rgba(14,30,38,.08); }
        .ql-mobile-brand { display: none; justify-content: center; margin-bottom: 14px; }
        .ql-switch { display: grid; grid-template-columns: 1fr 1fr; background: #f2efe8; border-radius: 9px; padding: 4px; margin-bottom: 22px; }
        .ql-switch button { border: 0; background: none; border-radius: 7px; padding: 8px 0; font-size: 13.5px; font-weight: 600; color: var(--ql-slate); cursor: pointer; }
        .ql-switch button.on { background: #fff; color: var(--ql-navy); box-shadow: 0 1px 3px rgba(14,30,38,.12); }
        .ql-card h1 { margin: 0; font-size: 22px; font-weight: 750; letter-spacing: -.3px; color: var(--ql-navy); }
        .ql-sub { margin: 6px 0 22px; font-size: 14px; color: var(--ql-slate); }
        .ql-alert { font-size: 13px; padding: 10px 12px; margin-bottom: 16px; border-radius: 8px; background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
        .ql-field { display: flex; flex-direction: column; gap: 6px; margin: 0 0 16px; min-width: 0; }
        .ql-field > span { font-size: 13px; font-weight: 600; color: #26363f; }
        .ql-field em { font-style: normal; font-weight: 400; color: var(--ql-slate); margin-left: 4px; }
        .ql-field small { font-size: 12px; color: var(--ql-slate); }
        .ql-field input { height: 42px; width: 100%; border: 1px solid var(--ql-line); border-radius: 7px; padding: 0 12px; font-size: 14px; color: var(--ql-navy); background: #fff; }
        .ql-field input:focus { outline: none; border-color: var(--ql-red); box-shadow: 0 0 0 3px rgba(232,56,29,.14); }
        .ql-two { display: grid; grid-template-columns: 1fr 1fr; gap: 0 12px; }
        .ql-hint { display: block; margin: -8px 0 16px; font-size: 12px; color: var(--ql-slate); }
        .ql-submit { width: 100%; height: 44px; margin-top: 6px; border: none; border-radius: 7px; background: var(--ql-red); color: #fff; font-size: 14.5px; font-weight: 650;
          display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; transition: background .15s ease; }
        .ql-submit:hover:not(:disabled) { background: var(--ql-red-hover); }
        .ql-submit:disabled { opacity: .7; cursor: default; }
        .ql-new { margin: 18px 0 0; font-size: 14px; color: var(--ql-slate); display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; text-align: center; }
        .ql-new a { color: var(--ql-red); font-weight: 600; text-decoration: none; }
        .ql-new a:hover { text-decoration: underline; }
        .ql-foot { margin: 10px 0 0; font-size: 12px; color: var(--ql-slate); }
        @media (max-width: 900px) {
          .ql { grid-template-columns: 1fr; }
          .ql-story { order: 2; padding: 36px 20px 44px; }
          .ql-panel { order: 1; padding: 28px 16px 20px; }
          .ql-mobile-brand { display: flex; }
          .ql-card { padding: 26px 20px; }
        }
        @media (max-width: 420px) { .ql-two { grid-template-columns: 1fr; } }
      `}</style>
    </main>
  );
}
